// src/features/developer/components/SimulatorPanel.tsx
import React, { useState, useEffect } from 'react'
import {
  startSimulator,
  stopSimulator,
  isSimulatorRunning,
} from '../../../../utils/simulateTransactions'
import {
  startOrderBot,
  stopOrderBot,
  isOrderBotRunning,
} from '../../../services/order/Bots/OrderSimulatorBot'
import { refreshTokenApi } from '../../auth/api/authApi'

export default function SimulatorPanel() {
  // ——————————————————————————————————————————————————————————————————
  // New: top‐level API JWT used by `fetchData` and for external testing
  // ——————————————————————————————————————————————————————————————————
  const [apiToken, setApiToken] = useState<string>(
    () => localStorage.getItem('jwt') || ''
  )
  const [refreshing, setRefreshing] = useState<boolean>(false)

  // Stock‐movement simulator state
  const [stockToken, setStockToken] = useState<string>(
    () => localStorage.getItem('simToken') || ''
  )
  const [stockEnabled, setStockEnabled] = useState<boolean>(
    () => localStorage.getItem('simEnabled') === 'true'
  )
  const [stockError, setStockError] = useState<string | null>(null)

  // Order simulator state
  const [orderToken, setOrderToken] = useState<string>(
    () => localStorage.getItem('orderSimToken') || ''
  )
  const [orderEnabled, setOrderEnabled] = useState<boolean>(
    () => localStorage.getItem('orderSimEnabled') === 'true'
  )
  const [orderError, setOrderError] = useState<string | null>(null)

  // Handler to refresh the token
  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      const { access_token: newToken } = await refreshTokenApi()
      setApiToken(newToken)
    } catch (err: any) {
      console.error('Failed to refresh token:', err)
      // you might want to show an error message here
    } finally {
      setRefreshing(false)
    }
  }

  // Start/stop stock simulator when toggled
  useEffect(() => {
    if (stockEnabled) {
      if (!stockToken.trim()) {
        setStockError('Please enter a valid JWT before enabling.')
        setStockEnabled(false)
        return
      }
      setStockError(null)
      startSimulator(stockToken.trim())
    } else {
      stopSimulator()
    }
    localStorage.setItem('simToken', stockToken)
    localStorage.setItem('simEnabled', stockEnabled.toString())
  }, [stockEnabled, stockToken])

  // Start/stop order simulator when toggled
  useEffect(() => {
    if (orderEnabled) {
      if (!orderToken.trim()) {
        setOrderError('Please enter a valid JWT before enabling.')
        setOrderEnabled(false)
        return
      }
      setOrderError(null)
      startOrderBot(orderToken.trim())
    } else {
      stopOrderBot()
    }
    localStorage.setItem('orderSimToken', orderToken)
    localStorage.setItem('orderSimEnabled', orderEnabled.toString())
  }, [orderEnabled, orderToken])

  // Keep stock toggle in sync if stopped externally
  useEffect(() => {
    if (!isSimulatorRunning() && stockEnabled) {
      setStockEnabled(false)
    }
  }, [stockEnabled])

  // Keep order toggle in sync if stopped externally
  useEffect(() => {
    if (!isOrderBotRunning() && orderEnabled) {
      setOrderEnabled(false)
    }
  }, [orderEnabled])

  return (
    <div className="space-y-8 bg-neutral-900 p-4 rounded-md">
      {/* ————————————————————————————————————————————————————————————
          API JWT (for external testing & used by fetchData)
      —————————————————————————————————————————————————————————————*/}
      <div className="flex flex-col pb-4 border-b border-neutral-700">
        <label htmlFor="api-token" className="text-neutral-200 font-medium">
          API JWT
        </label>
        <div className="mt-1 flex space-x-2 w-full">
          <input
          id="api-token"
          type="text"
          value={apiToken}
          onChange={(e) => setApiToken(e.target.value)}
          placeholder="Paste or view your current JWT"
          className="flex-1 bg-neutral-800 text-neutral-100 px-3 py-2 rounded-md"
        />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={
              `px-3 py-2 rounded-md text-sm font-medium
               ${refreshing
                 ? 'bg-neutral-700 text-neutral-500 cursor-wait'
                 : 'bg-emerald-600 hover:bg-emerald-500 text-white'}`
            }
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
        <p className="mt-1 text-xs text-neutral-500">
          Use this token to test APIs externally; it’s also the token your app uses under the hood.
        </p>
      </div>

      {/* Stock‐movement Simulator */}
      <div className="space-y-4">
        <h2 className="text-lg font-medium text-neutral-200">Stock Simulator</h2>
        <div className="flex items-center space-x-2">
          <label htmlFor="stock-toggle" className="flex items-center cursor-pointer">
            <input
              id="stock-toggle"
              type="checkbox"
              className="sr-only peer"
              checked={stockEnabled}
              onChange={(e) => setStockEnabled(e.target.checked)}
            />
            <div className="w-11 h-6 bg-neutral-700 rounded-full peer-checked:bg-green-600 relative after:absolute after:top-0.5 after:left-1 after:bg-white after:rounded-full after:h-5 after:w-5 peer-checked:after:translate-x-full peer-checked:after:border-white" />
          </label>
          <span className={`text-sm ${stockEnabled ? 'text-green-400' : 'text-rose-400'}`}>
            {stockEnabled ? 'On' : 'Off'}
          </span>
        </div>
        <div className="flex flex-col">
          <label htmlFor="stock-token" className="text-neutral-200">JWT:</label>
          <input
            id="stock-token"
            type="text"
            value={stockToken}
            onChange={(e) => setStockToken(e.target.value)}
            placeholder="Paste JWT here"
            className="mt-1 bg-neutral-800 text-neutral-100 px-3 py-2 rounded-md"
          />
        </div>
        {stockError && <p className="text-sm text-rose-400">{stockError}</p>}
        <p className="text-sm text-neutral-400">
          {stockEnabled
            ? 'Posting stock‐movement events every 1–5 seconds.'
            : 'Stock simulator is stopped.'}
        </p>
      </div>

      {/* Order Simulator */}
      <div className="space-y-4">
        <h2 className="text-lg font-medium text-neutral-200">Order Simulator</h2>
        <div className="flex items-center space-x-2">
          <label htmlFor="order-toggle" className="flex items-center cursor-pointer">
            <input
              id="order-toggle"
              type="checkbox"
              className="sr-only peer"
              checked={orderEnabled}
              onChange={(e) => setOrderEnabled(e.target.checked)}
            />
            <div className="w-11 h-6 bg-neutral-700 rounded-full peer-checked:bg-green-600 relative after:absolute after:top-0.5 after:left-1 after:bg-white after:rounded-full after:h-5 after:w-5 peer-checked:after:translate-x-full peer-checked:after:border-white" />
          </label>
          <span className={`text-sm ${orderEnabled ? 'text-green-400' : 'text-rose-400'}`}>
            {orderEnabled ? 'On' : 'Off'}
          </span>
        </div>
        <div className="flex flex-col">
          <label htmlFor="order-token" className="text-neutral-200">JWT:</label>
          <input
            id="order-token"
            type="text"
            value={orderToken}
            onChange={(e) => setOrderToken(e.target.value)}
            placeholder="Paste JWT here"
            className="mt-1 bg-neutral-800 text-neutral-100 px-3 py-2 rounded-md"
          />
        </div>
        {orderError && <p className="text-sm text-rose-400">{orderError}</p>}
        <p className="text-sm text-neutral-400">
          {orderEnabled
            ? 'Posting fake orders every 3–10 seconds.'
            : 'Order simulator is stopped.'}
        </p>
      </div>
    </div>
  )
}
